import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Flex, IconButton, Skeleton, Text, useColorMode, useMediaQuery,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  LuCheck, LuChevronDown, LuChevronLeft, LuChevronRight, LuTriangleAlert,
} from 'react-icons/lu';
import i18n from '../../i18n';
import {
  getAccounts, fetchSpendingTrend, getAccountBalanceHistory, getReferenceData, groupMovements, searchMovements,
} from '../../api/endpointsBackend';
import { ConvertedAmountsProvider } from '../../contexts/ConvertedAmountsContext';
import { useDisplayCurrency } from '../../contexts/DisplayCurrencyContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import useGoalsEnabled from '../../hooks/useGoalsEnabled';
import { useFilters } from '../../contexts/FilterContext';
import {
  useDashboardData, transformGroupedDataToMetrics, buildCategoryMap, buildConvertedCategoryBreakdown,
} from '../../hooks/useDashboardData';
import { useConvertedTotal } from '../../hooks/useConvertedTotal';
import { useGoalsView } from '../../hooks/useGoalsView';
import useOnboarding from '../../hooks/useOnboarding';
import useReviewCount from '../../hooks/useReviewCount';
import usePhoneHomeCurrency from '../../hooks/usePhoneHomeCurrency';
import { DATE_MODES, getCurrentDateRange, getMonthDateRange } from '../../utils/dateUtils';
import { monthLong, monthShort } from '../../utils/intlFormatters';
import { todayLocalISO } from '../../utils/localDate';
import { spendingPace, cumulativeAt } from '../../utils/spendingPace';
import { resolveHomeCurrency } from '../../utils/homeCurrency';
import { formatFigure, formatPhoneMoney } from '../../utils/formatters';
import { categoryTint } from '../../utils/categoryTint';
import TimeSeriesChart, { chartColor } from '../shared/TimeSeriesChart';
import UnconvertibleNote from '../shared/UnconvertibleNote';
import Figure from '../shared/Figure';
import GoalIcon from '../goals/GoalIcon';
import PhoneMovementRow from './PhoneMovementRow';
import PhoneSheet from './PhoneSheet';
import PhoneReviewSheet from './PhoneReviewSheet';
import { usePhoneAccountSheet } from './PhoneAccountSheet';
import { usePhoneShell, usePhoneTopBar } from './PhoneShellContext';
import { OPEN_SETUP_EVENT } from './events';

const RECENT_COUNT = 5;
const RECENT_DAYS = 3;
const TOP_CATEGORIES = 5;
const TOP_GOALS = 2;

// Home's figures drop the cents from four digits up: in a total or a balance
// they are noise, and they would push a line past the screen's width.
const round = (amount) => (Math.abs(amount) >= 1000 ? Math.round(amount) : amount);
const money = (currency, amount, options) => formatPhoneMoney(currency, round(Number(amount) || 0), options);
const hasCents = (amount) => Math.round(Math.abs(round(Number(amount) || 0)) * 100) % 100 !== 0;
const TWO_DECIMALS = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// A column of amounts keeps two decimals on every line when any line has
// cents, as a list of rows does: "USD 8.00" over "USD 5.88", not "USD 8".
const columnMoney = (currency, amount, cents) => (cents
  ? `${currency ? `${currency} ` : ''}${TWO_DECIMALS.format(Math.abs(round(Number(amount) || 0)))}`
  : money(currency, amount));
// "+6,200,022": a signed figure without its code, where the hero above
// already names the currency and the line must fit 320px.
const signedFigure = (amount) => {
  const value = round(Number(amount) || 0);
  return `${value < 0 ? '−' : value > 0 ? '+' : ''}${formatFigure(value)}`;
};

// The narrow screen (320px Display Zoom).
const NARROW_QUERY = '(max-width: 359.98px)';

// "Only <code> movements" lasts the session, per workspace: it outlives a
// tab switch (Home unmounts) and is gone when the app is closed.
const onlyKey = (workspaceId) => `mk.homeOnlyCurrency.${workspaceId}`;
function readOnlyCurrency(workspaceId) {
  try {
    return sessionStorage.getItem(onlyKey(workspaceId)) || null;
  } catch {
    return null;
  }
}
function writeOnlyCurrency(workspaceId, code) {
  try {
    if (code) sessionStorage.setItem(onlyKey(workspaceId), code);
    else sessionStorage.removeItem(onlyKey(workspaceId));
  } catch {
    /* private mode: the choice lasts while Home is on screen */
  }
}

// Once the display currency has had its chance to settle, Home fetches at
// once on every later visit. Before that, it waits, rather than asking for
// one currency's figures and then again for the converted ones.
let currencySettled = false;

// A month name inside a sentence: "September" in English, "septiembre" in
// Spanish, which does not capitalise months mid-sentence.
const monthWord = (idx) => {
  const word = monthLong(idx);
  return i18n.language?.startsWith('es') ? word.toLocaleLowerCase('es') : word;
};

const pad = (n) => String(n).padStart(2, '0');
const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();

function daysAgoISO(days) {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return todayLocalISO(date);
}

// Relative luminance of a #RRGGBB colour, 0 (black) to 1 (white).
function luminance(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return null;
  const [r, g, b] = [0, 2, 4].map((i) => {
    const c = parseInt(m[1].slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** A section heading with an optional "See all ›" on the right. */
function Caption({ children, action, onAction, mt = '8px' }) {
  return (
    <Flex align="center" justify="space-between" minH="44px" pl="16px" pr="4px" mt={mt}>
      <Text as="h2" fontSize="13px" lineHeight="18px" fontWeight="600" letterSpacing="0.06em" textTransform="uppercase" color="phone.muted">
        {children}
      </Text>
      {action && <LinkButton onClick={onAction}>{action}</LinkButton>}
    </Flex>
  );
}

function LinkButton({ children, onClick, ...rest }) {
  return (
    <Button
      variant="ghost"
      color="phone.link"
      fontSize="14px"
      fontWeight="500"
      minH="44px"
      px="12px"
      _hover={{ bg: 'transparent' }}
      _active={{ bg: 'redesign.bg.hover' }}
      onClick={onClick}
      {...rest}
    >
      {children}
    </Button>
  );
}

/** The month shown, with ‹ › to step it; the next month stops at this one. */
function PeriodBar({ month, onStep, canNext }) {
  const { t } = useTranslation('dashboard');
  // Below 360px the month is abbreviated ("Sep 2026"), so "Septiembre 2026"
  // does not crowd the chevrons.
  const [narrow] = useMediaQuery('(max-width: 359px)', { ssr: false });
  // After a touch tap the ghost button would keep its hover grey.
  const chevron = {
    variant: 'ghost',
    minW: '44px',
    minH: '44px',
    color: 'redesign.text',
    _hover: { bg: 'transparent' },
    _active: { bg: 'redesign.bg.hover' },
  };
  return (
    <Flex align="center" justify="center" h="44px">
      <IconButton
        {...chevron}
        icon={<LuChevronLeft size={22} />}
        aria-label={t('phone.period.previous')}
        onClick={() => onStep(-1)}
      />
      <Text as="span" fontSize="17px" fontWeight="600" color="redesign.heading" px="8px" minW={narrow ? undefined : '150px'} textAlign="center" aria-live="polite">
        {`${narrow ? monthShort(month.month) : monthLong(month.month)} ${month.year}`}
      </Text>
      <IconButton
        {...chevron}
        icon={<LuChevronRight size={22} />}
        aria-label={t('phone.period.next')}
        isDisabled={!canNext}
        onClick={() => onStep(1)}
      />
    </Flex>
  );
}

/** Shown while the account step is pending: the one step a phone can do. */
function SetupChip() {
  const { t } = useTranslation('dashboard');
  const { steps, doneCount, totalCount, loading, mayHold } = useOnboarding();
  // Onboarding's own users only: for anyone else this leads into a flow that
  // seeds an account they never asked for.
  const accountPending = mayHold && !loading && steps.some((s) => s.key === 'account' && !s.done);
  if (!accountPending) return null;
  return (
    <Box px="16px" pt="4px">
      <Box
        as="button"
        type="button"
        onClick={() => window.dispatchEvent(new CustomEvent(OPEN_SETUP_EVENT))}
        display="flex"
        alignItems="center"
        gap="6px"
        h="44px"
        px="14px"
        borderRadius="full"
        bg="redesign.bg.accent"
        color="redesign.heading"
        fontSize="15px"
        fontWeight="600"
      >
        {`${t('phone.setup')} · ${t('phone.setupProgress', { done: doneCount, total: totalCount })}`}
        <Box as={LuChevronRight} boxSize="18px" />
      </Box>
    </Box>
  );
}

/**
 * The daily cumulative spend for the chart: last month dashed across the
 * whole month, this month solid up to today (or the whole month once it is
 * past). Both are read at each day of this month, last month's day clamped
 * to its own length.
 */
function buildChartSeries(trend, month, today) {
  if (!trend) return null;
  const days = daysInMonth(month.year, month.month);
  const prevYear = month.month === 0 ? month.year - 1 : month.year;
  const prevMonth = month.month === 0 ? 11 : month.month - 1;
  const prevDays = daysInMonth(prevYear, prevMonth);
  const monthKey = `${month.year}-${pad(month.month + 1)}`;
  const lastDay = today.slice(0, 7) === monthKey ? Number(today.slice(8, 10)) : days;

  const current = [];
  for (let d = 1; d <= lastDay; d += 1) {
    current.push(cumulativeAt(trend.current?.points, `${monthKey}-${pad(d)}`));
  }
  const previous = [];
  if (trend.previous?.points?.length) {
    for (let d = 1; d <= days; d += 1) {
      previous.push(cumulativeAt(trend.previous.points, `${prevYear}-${pad(prevMonth + 1)}-${pad(Math.min(d, prevDays))}`));
    }
  }
  return { days, current, previous };
}

const chartIsEmpty = (chart) => Boolean(chart) && [...chart.current, ...chart.previous].every((v) => !v);

// Where today's dot sits, in % of the chart box. Drawn in HTML rather than by
// the chart, whose stretched viewBox turns a circle into an ellipse on a wide
// (landscape) screen. Mirrors the chart's own scaling to the data range.
function chartEndDot(chart) {
  if (!chart?.current.length || chart.days < 2) return null;
  const all = [...chart.current, ...chart.previous];
  const rawMax = Math.max(...all);
  const rawMin = Math.min(...all);
  const pad = rawMax === rawMin ? Math.max(Math.abs(rawMax) * 0.1, 1) : 0;
  const min = rawMin - pad;
  const range = (rawMax + pad) - min || 1;
  const last = chart.current[chart.current.length - 1];
  return {
    x: ((chart.current.length - 1) / (chart.days - 1)) * 100,
    y: 100 - (((last - min) / range) * 85 + 7.5),
  };
}

function Hero({
  month, today, currency, expenses, income, loading, trend,
}) {
  const { t } = useTranslation('dashboard');
  const [narrow] = useMediaQuery(NARROW_QUERY, { ssr: false });
  const prevIdx = month.month === 0 ? 11 : month.month - 1;
  const monthStart = `${month.year}-${pad(month.month + 1)}-01`;
  const pace = spendingPace(trend, monthStart, today);
  const chart = useMemo(() => buildChartSeries(trend, month, today), [trend, month, today]);

  let paceText = null;
  if (pace) {
    const values = { amount: money(currency, Math.abs(pace.diff)), month: monthWord(prevIdx) };
    const suffix = pace.atThisPoint ? '' : 'Whole';
    if (Math.round(pace.diff) === 0) paceText = t(`phone.pace.same${suffix}`, values);
    else paceText = t(`phone.pace.${pace.diff > 0 ? 'more' : 'less'}${suffix}`, values);
  }
  const net = (income || 0) - (expenses || 0);
  const empty = chartIsEmpty(chart);
  const dot = empty ? null : chartEndDot(chart);
  // At 320px the code goes from these two: the figure above names it.
  const lineMoney = (amount) => (narrow ? signedFigure(amount) : money(currency, amount, { signed: true }));
  const lineSkeleton = <Skeleton as="span" display="inline-block" h="14px" w="84px" verticalAlign="middle" borderRadius="4px" startColor="redesign.bg.hover" endColor="redesign.border" />;

  return (
    <Box mx="16px" mt="4px" p="16px" borderRadius="16px" bg="redesign.bg.surface" border="1px solid" borderColor="redesign.border">
      <Flex justify="space-between" align="center" minH="18px" gap="8px">
        <Text fontSize="13px" lineHeight="18px" color="phone.muted" noOfLines={1}>
          {t('phone.hero.spentIn', { month: monthWord(month.month) })}
        </Text>
        <Flex gap="12px" fontSize="11px" color="phone.muted" align="center" flexShrink={0} aria-hidden="true">
          <Flex align="center" gap="5px">
            <Box w="14px" h="2px" borderRadius="1px" bg="redesign.accent.blue" />
            {monthShort(month.month)}
          </Flex>
          {chart?.previous.length > 0 && (
            <Flex align="center" gap="5px">
              <Box w="14px" h="2px" bgImage="repeating-linear-gradient(90deg, var(--chakra-colors-phone-muted) 0 3px, transparent 3px 5px)" />
              {monthShort(prevIdx)}
            </Flex>
          )}
        </Flex>
      </Flex>

      <Skeleton isLoaded={!loading} mt="4px" borderRadius="8px" startColor="redesign.bg.hover" endColor="redesign.border">
        {/* The code small and raised before the number, as every lead figure
            reads; Figure's lg is the hero's own 34px. */}
        <Figure size="lg" textStyle="phoneHero" display="block" code={currency} amount={formatFigure(round(expenses || 0))} />
      </Skeleton>

      {/* The pace line keeps its room only while the trend loads. */}
      {(paceText || !trend) && (
        <Box minH="18px" mt="6px">
          {paceText && (
            <Text fontSize="13px" lineHeight="18px" color={pace.diff > 0 ? 'phone.expense' : 'phone.income'}>
              {paceText}
            </Text>
          )}
        </Box>
      )}

      <Box mt="8px" h="88px" aria-hidden="true" position="relative">
        {dot && (
          <Box
            position="absolute"
            w="13px"
            h="13px"
            borderRadius="full"
            border="2px solid"
            borderColor="redesign.bg.surface"
            bg="redesign.accent.blue"
            transform="translate(-50%, -50%)"
            style={{ left: `${dot.x}%`, top: `${dot.y}%` }}
          />
        )}
        {/* A month with nothing spent has no line to draw: a flat line would
            sit mid-chart, reading as a level rather than zero. */}
        {empty && (
          <Flex h="100%" align="center" justify="center" fontSize="13px" color="phone.muted">
            {t('phone.hero.noSpending', { month: monthWord(month.month) })}
          </Flex>
        )}
        {chart && !empty && (
          <TimeSeriesChart
            height={88}
            my={0}
            interactive={false}
            showZeroLine={false}
            slots={chart.days}
            series={[
              ...(chart.previous.length
                ? [{ key: 'previous', values: chart.previous, color: chartColor('phone.muted'), strokeWidth: 1.5, dashed: true, endDot: false }]
                : []),
              { key: 'current', values: chart.current, color: chartColor('redesign.accent.blue'), strokeWidth: 2, endDot: false },
            ]}
          />
        )}
      </Box>
      <Flex justify="space-between" fontSize="11px" lineHeight="14px" color="phone.muted" mt="4px" aria-hidden="true">
        <span>1</span>
        <span>15</span>
        <span>{daysInMonth(month.year, month.month)}</span>
      </Flex>

      <Flex
        mt="12px"
        pt="12px"
        borderTop="1px solid"
        borderColor="redesign.border"
        justify="space-between"
        align="baseline"
        flexWrap="wrap"
        columnGap="12px"
        rowGap="4px"
        lineHeight="20px"
      >
        <Box as="span" whiteSpace="nowrap">
          <Box as="span" fontSize="13px" color="phone.muted" mr="6px">{t('phone.hero.income')}</Box>
          <Box as="span" fontSize="14px" fontWeight="600" color="phone.income">{loading ? lineSkeleton : lineMoney(income || 0)}</Box>
        </Box>
        <Box as="span" whiteSpace="nowrap">
          <Box as="span" fontSize="13px" color="phone.muted" mr="6px">{t('phone.hero.net')}</Box>
          <Box as="span" fontSize="14px" fontWeight="600" color="redesign.text">{loading ? lineSkeleton : lineMoney(net)}</Box>
        </Box>
      </Flex>
    </Box>
  );
}

function ReviewRow({ onOpen }) {
  const { t } = useTranslation('dashboard');
  const { fixable, waiting, loading } = useReviewCount();
  const [narrow] = useMediaQuery(NARROW_QUERY, { ssr: false });
  // Until a first count arrives the row claims nothing: a "Nothing to review"
  // that flips to "4 movements to review" reads as a glitch.
  const pending = loading;
  const clear = !pending && fixable === 0;
  let icon = LuTriangleAlert;
  let iconColor = 'phone.alert';
  if (pending) iconColor = 'phone.muted';
  else if (clear) { icon = LuCheck; iconColor = 'phone.income'; }
  return (
    <Box px="16px" mt="12px">
      <Box
        as="button"
        type="button"
        onClick={onOpen}
        w="100%"
        minH="56px"
        py="8px"
        borderRadius="14px"
        bg={clear || pending ? 'redesign.bg.surface' : 'phone.alertBg'}
        border="1px solid"
        borderColor={clear || pending ? 'redesign.border' : 'transparent'}
        display="grid"
        gridTemplateColumns="22px minmax(0, 1fr) 20px"
        columnGap="12px"
        alignItems="center"
        pl="14px"
        pr="12px"
        textAlign="left"
        aria-busy={pending || undefined}
        _active={{ opacity: 0.8 }}
      >
        <Box as={icon} boxSize="22px" color={iconColor} />
        <Box minW={0}>
          {pending ? (
            <Skeleton h="16px" w="60%" my="2px" borderRadius="4px" startColor="redesign.bg.hover" endColor="redesign.border" />
          ) : (
            <Box fontSize={narrow ? '15px' : '16px'} fontWeight="600" lineHeight="20px" color="redesign.heading">
              {clear ? t('phone.review.nothing') : t('phone.review.toReview', { count: fixable })}
            </Box>
          )}
          {!pending && waiting > 0 && (
            <Box fontSize="13px" lineHeight="17px" mt="1px" color="phone.muted">
              {t(clear ? 'phone.review.waitingOnly' : 'phone.review.waiting', { count: waiting })}
            </Box>
          )}
        </Box>
        <Box as={LuChevronRight} boxSize="20px" color="phone.muted" />
      </Box>
    </Box>
  );
}

/**
 * Every account's balance at the end of the month, one card per account and
 * currency, ordered by how many movements it had this month.
 */
function Balances({ range, onlyCurrency, reloadKey, onOpen, onSeeAll }) {
  const { t } = useTranslation('dashboard');
  const [cards, setCards] = useState(null);

  useEffect(() => {
    let active = true;
    Promise.all([
      groupMovements([], ['currency', 'account_id'], range.start_date, range.end_date),
      getAccountBalanceHistory(range.start_date, range.end_date, null, { includeWindowOpening: true }),
      getAccounts(),
    ])
      .then(([counts, buckets, accounts]) => {
        if (!active) return;
        const byKey = new Map();
        const entry = (accountId, currency) => {
          const key = `${accountId}|${currency}`;
          if (!byKey.has(key)) byKey.set(key, { accountId, currency, balance: 0, count: 0, seen: false });
          return byKey.get(key);
        };
        // The window's opening plus each period's change is the balance at its end.
        (buckets || []).forEach((b) => {
          const e = entry(b.account_id, b.currency);
          e.balance += b.total || 0;
          e.seen = true;
        });
        (counts || []).forEach((c) => {
          if (c.account_id == null) return;
          entry(c.account_id, c.currency).count += c.num_movements || 0;
        });
        const active_ = new Map((accounts || []).filter((a) => a.is_active !== false).map((a) => [a.id, a]));
        const list = [...byKey.values()]
          .filter((e) => active_.has(e.accountId) && (e.count > 0 || Math.abs(e.balance) >= 0.005))
          .map((e) => {
            const account = active_.get(e.accountId);
            // The ledger already keeps a card's debt negative and a credit in
            // its favour positive, so the balance shows as it stands.
            return { ...e, name: account.name, balance: e.balance };
          })
          .sort((a, b) => b.count - a.count || Math.abs(b.balance) - Math.abs(a.balance));
        setCards(list);
      })
      .catch(() => { if (active) setCards((prev) => prev || []); });
    return () => { active = false; };
  }, [range.start_date, range.end_date, reloadKey]);

  const shown = (cards || []).filter((c) => !onlyCurrency || c.currency === onlyCurrency);

  return (
    <>
      <Caption action={t('phone.seeAll')} onAction={onSeeAll}>{t('phone.balances')}</Caption>
      <Box position="relative">
        <Flex
          as="ul"
          listStyleType="none"
          gap="10px"
          px="16px"
          overflowX="auto"
          sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
        >
          {cards === null && [0, 1, 2].map((i) => (
            <Skeleton key={i} as="li" flex="none" w="140px" h="72px" borderRadius="14px" startColor="redesign.bg.card" endColor="redesign.border" />
          ))}
          {cards !== null && shown.length === 0 && (
            <Text as="li" fontSize="15px" color="phone.muted" py="12px">{t('phone.noBalances')}</Text>
          )}
          {shown.map((card) => (
            <Box as="li" key={`${card.accountId}|${card.currency}`} flex="none">
              <Box
                as="button"
                type="button"
                onClick={() => onOpen(card)}
                w="140px"
                h="72px"
                borderRadius="14px"
                bg="redesign.bg.surface"
                border="1px solid"
                borderColor="redesign.border"
                p="12px"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                textAlign="left"
                _active={{ bg: 'redesign.bg.hover' }}
              >
                <Box w="100%" fontSize="13px" lineHeight="17px" color="phone.muted" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                  {card.name}
                </Box>
                <Box w="100%" fontSize="15px" lineHeight="20px" fontWeight="600" color="redesign.heading" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                  {money(card.currency, card.balance)}
                </Box>
              </Box>
            </Box>
          ))}
          {/* Keeps the last card clear of the fade. */}
          <Box as="li" aria-hidden="true" flex="none" w="6px" />
        </Flex>
        <Box
          position="absolute"
          top={0}
          right={0}
          w="36px"
          h="100%"
          pointerEvents="none"
          bgGradient="linear(to-r, transparent, redesign.bg.page)"
        />
      </Box>
    </>
  );
}

/** The last movements created, whatever their date: this morning's imports. */
function Recent({
  onlyCurrency, homeCurrency, reloadKey, ready, onSeeAll,
}) {
  const { t } = useTranslation('dashboard');
  const { openMovement } = usePhoneShell();
  const [rows, setRows] = useState(null);

  useEffect(() => {
    if (!ready) return undefined;
    let active = true;
    const filters = [
      { entity: 'Movement', column: 'created_at', operator: 'gte', values: [daysAgoISO(RECENT_DAYS)] },
      { entity: 'Movement', column: 'is_ignored', operator: 'eq', values: [false] },
      ...(onlyCurrency ? [{ entity: 'Movement', column: 'currency', operator: 'in', values: [onlyCurrency] }] : []),
    ];
    // Search windows by movement date, not by rows, and has no row limit, so
    // it is asked for a wide window and the newest five are picked here.
    searchMovements(filters, [{ entity: 'Movement', column: 'created_at', sort: 'desc' }], null, null, 50, 0)
      .then((res) => {
        if (!active) return;
        const items = [...(res?.items || [])]
          .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
          .slice(0, RECENT_COUNT);
        setRows(items);
      })
      .catch(() => { if (active) setRows((prev) => prev || []); });
    return () => { active = false; };
  }, [onlyCurrency, reloadKey, ready]);

  return (
    <>
      <Caption action={t('phone.seeAll')} onAction={onSeeAll}>{t('phone.recent')}</Caption>
      {rows === null && <Skeleton mx="16px" h="128px" borderRadius="12px" startColor="redesign.bg.card" endColor="redesign.border" />}
      {rows !== null && rows.length === 0 && (
        <Text fontSize="15px" color="phone.muted" px="16px" py="8px">{t('phone.noRecent')}</Text>
      )}
      {rows?.map((m) => (
        <PhoneMovementRow key={m.id} movement={m} homeCurrency={homeCurrency} onClick={() => openMovement(m.id)} />
      ))}
      {rows?.length > 0 && <Box px="4px"><LinkButton onClick={onSeeAll}>{t('phone.seeAll')}</LinkButton></Box>}
    </>
  );
}

function CategoryShare({
  row, total, currency, cents, onClick, divided,
}) {
  const { colorMode } = useColorMode();
  const pct = total > 0 ? (row.amount / total) * 100 : 0;
  const lum = luminance(row.color);
  // A category colour close to the card's own would vanish as a bar.
  const outline = lum != null && (colorMode === 'dark' ? lum < 0.06 : lum > 0.8);
  return (
    <Box
      as={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      w="100%"
      display="grid"
      gridTemplateColumns="32px minmax(0, 1fr)"
      columnGap="12px"
      alignItems="center"
      minH="56px"
      px="14px"
      py="8px"
      textAlign="left"
      _active={onClick ? { bg: 'redesign.bg.hover' } : undefined}
      borderTop={divided ? '1px solid' : undefined}
      borderColor="redesign.border"
    >
      <Flex w="32px" h="32px" borderRadius="full" align="center" justify="center" fontSize="16px" bg={row.color ? categoryTint(row.color, colorMode).bg : 'redesign.border'}>
        {row.icon || '❓'}
      </Flex>
      <Box minW={0}>
        <Flex justify="space-between" align="baseline" lineHeight="20px">
          <Box as="span" fontSize="16px" color="redesign.heading" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">{row.category}</Box>
          <Box as="span" fontSize="15px" fontWeight="500" color="redesign.heading" whiteSpace="nowrap" ml="10px">{columnMoney(currency, row.amount, cents)}</Box>
        </Flex>
        <Flex align="center" gap="8px" mt="6px">
          <Box flex="1" h="4px" borderRadius="2px" bg="redesign.border">
            <Box
              h="4px"
              borderRadius="2px"
              w={`${Math.max(pct, 1)}%`}
              bg={row.color || 'redesign.accent.blue'}
              boxShadow={outline ? '0 0 0 1px var(--chakra-colors-phone-muted)' : undefined}
            />
          </Box>
          <Box as="span" fontSize="13px" lineHeight="14px" color="phone.muted" w="36px" textAlign="right">
            {pct > 0 && pct < 1 ? '<1%' : `${Math.round(pct)}%`}
          </Box>
        </Flex>
      </Box>
    </Box>
  );
}

function ListCard({ children }) {
  return (
    <Box mx="16px" borderRadius="14px" bg="redesign.bg.surface" border="1px solid" borderColor="redesign.border" overflow="hidden">
      {children}
    </Box>
  );
}

function WhereItWent({
  rows, total, currency, loading, onCategory, onAll,
}) {
  const { t } = useTranslation('dashboard');
  if (!loading && rows.length === 0) return null;
  const cents = rows.some((row) => hasCents(row.amount));
  return (
    <>
      {/* Right under Recent's "See all ›" row, which already leaves the air. */}
      <Caption mt="0">{t('phone.whereItWent')}</Caption>
      {loading ? (
        <Skeleton mx="16px" h="280px" borderRadius="14px" startColor="redesign.bg.card" endColor="redesign.border" />
      ) : (
        <ListCard>
          {rows.map((row, i) => (
            <CategoryShare
              key={row.category_id ?? 'none'}
              divided={i > 0}
              row={row}
              total={total}
              currency={currency}
              cents={cents}
              onClick={row.category_id != null ? () => onCategory(row) : undefined}
            />
          ))}
        </ListCard>
      )}
      <Box px="4px"><LinkButton onClick={onAll}>{t('phone.allCategories')}</LinkButton></Box>
    </>
  );
}

function Goals({ reloadKey }) {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const goalsEnabled = useGoalsEnabled();
  const { can } = useWorkspace();
  const enabled = goalsEnabled && can('goal.read');
  const { rows, loading, refetch } = useGoalsView({ includeArchived: false, enabled });
  const lastReload = useRef(reloadKey);

  useEffect(() => {
    if (lastReload.current === reloadKey) return;
    lastReload.current = reloadKey;
    if (enabled) refetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const goals = rows.filter((g) => g.status === 'active').slice(0, TOP_GOALS);
  if (!enabled || (!loading && goals.length === 0)) return null;

  return (
    <>
      <Caption mt="0">{t('phone.goals')}</Caption>
      {loading && goals.length === 0 ? (
        <Skeleton mx="16px" h="104px" borderRadius="14px" startColor="redesign.bg.card" endColor="redesign.border" />
      ) : (
        <ListCard>
          {goals.map((goal, i) => {
            const s = goal.summary;
            const pct = s ? Math.max(0, Math.min(100, s.pct || 0)) : 0;
            return (
              <Box
                key={goal.id}
                as="button"
                type="button"
                // Goals opens this goal's read-only sheet from `goal_id`.
                onClick={() => navigate(`/goals?goal_id=${goal.id}`)}
                w="100%"
                display="grid"
                gridTemplateColumns="32px minmax(0, 1fr)"
                columnGap="12px"
                alignItems="center"
                minH="56px"
                px="14px"
                py="8px"
                textAlign="left"
                _active={{ bg: 'redesign.bg.hover' }}
                borderTop={i > 0 ? '1px solid' : undefined}
                borderColor="redesign.border"
              >
                <GoalIcon goal={goal} size={32} />
                <Box minW={0}>
                  <Flex justify="space-between" align="baseline" lineHeight="19px">
                    <Box as="span" fontSize="15px" fontWeight="500" color="redesign.heading" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">{goal.name}</Box>
                    <Box as="span" fontSize="13px" color="phone.muted" ml="10px">{s ? `${Math.round(s.pct || 0)}%` : '—'}</Box>
                  </Flex>
                  {s && (
                    <Box fontSize="13px" lineHeight="16px" color="phone.muted" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                      {t('phone.goalSavedOf', { saved: money(s.currency || goal.currency, s.saved || 0), target: formatFigure(round(s.target ?? goal.target_amount)) })}
                    </Box>
                  )}
                  <Box h="4px" borderRadius="2px" bg="redesign.border" mt="4px">
                    <Box h="4px" borderRadius="2px" w={`${pct}%`} bg={goal.color || 'redesign.accent.blue'} />
                  </Box>
                </Box>
              </Box>
            );
          })}
        </ListCard>
      )}
      <Box px="4px"><LinkButton onClick={() => navigate('/goals')}>{t('phone.allGoals')}</LinkButton></Box>
    </>
  );
}

/**
 * "Show amounts in": everything converted into the display currency (app-wide,
 * for the session), or only one currency's movements (Home's own filter).
 */
function CurrencySheet({
  isOpen, onClose, target, activeCurrencies, converted, onlyCurrency, onConverted, onOnly,
}) {
  const { t } = useTranslation('dashboard');
  const options = [
    ...(target ? [{ key: 'all', label: t('phone.currency.everything', { currency: target }), selected: converted, pick: onConverted }] : []),
    ...activeCurrencies.map((code) => ({
      key: code,
      label: t('phone.currency.only', { currency: code }),
      selected: !converted && onlyCurrency === code,
      pick: () => onOnly(code),
    })),
  ];
  return (
    <PhoneSheet id="home-currency" isOpen={isOpen} onClose={onClose} size="content">
      <Box px="16px" pb="8px">
        <Text as="h2" textStyle="phoneTitle" color="redesign.heading" mt="4px" mb="16px">{t('phone.currency.title')}</Text>
        <Box role="radiogroup" aria-label={t('phone.currency.title')} borderRadius="14px" bg="redesign.bg.card" border="1px solid" borderColor="redesign.border" overflow="hidden">
          {options.map((o, i) => (
            <Box
              key={o.key}
              as="button"
              type="button"
              role="radio"
              aria-checked={o.selected}
              onClick={() => { o.pick(); onClose(); }}
              w="100%"
              minH="52px"
              px="14px"
              py="10px"
              gap="10px"
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              textAlign="left"
              fontSize="17px"
              lineHeight="22px"
              fontWeight={o.selected ? '600' : '400'}
              color="redesign.heading"
              _active={{ bg: 'redesign.bg.hover' }}
              borderTop={i > 0 ? '1px solid' : undefined}
              borderColor="redesign.border"
            >
              {o.label}
              {o.selected && <Box as={LuCheck} boxSize="20px" color="phone.link" flexShrink={0} />}
            </Box>
          ))}
        </Box>
        <Text fontSize="15px" lineHeight="20px" color="phone.muted" mt="14px">{t('phone.currency.note')}</Text>
      </Box>
    </PhoneSheet>
  );
}

// Reports a converted total up to Home. Remounted (a new key) to refetch, so
// Home can keep showing the previous figures until the new ones land.
function ConvertedLoader({ range, enabled, filters, onData }) {
  const { data, loading } = useConvertedTotal(filters, {
    startDate: range.start_date,
    endDate: range.end_date,
    dimensions: ['category_id', 'type'],
    enabled,
  });
  useEffect(() => {
    if (!loading && data) onData(data);
  }, [data, loading, onData]);
  return null;
}

const NO_FILTERS = [];

/**
 * Home on the phone: where the month stands, what needs the user, and what
 * each account holds, in one scroll.
 *
 * Home ignores FilterContext. Its only inputs are its own month and its
 * currency pill, so a filter set on Movements never changes these figures.
 * Each section loads on its own, and everything refetches when the tab
 * regains focus and after every phone save.
 */
export default function PhoneHome() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const { refreshSignal } = usePhoneShell();
  const { displayCurrency, setDisplayCurrency, activeCurrencies } = useDisplayCurrency();
  const { homeCurrency, country } = usePreferences();
  const { currentId } = useWorkspace();
  const { clearFilters, setDateRange, addFilter } = useFilters();
  // Recent's rows drop their code as every phone list's rows do.
  const rowHomeCurrency = usePhoneHomeCurrency();
  const { openAccount, accountSheet } = usePhoneAccountSheet();

  const today = todayLocalISO();
  const [month, setMonth] = useState(() => ({ year: Number(today.slice(0, 4)), month: Number(today.slice(5, 7)) - 1 }));
  const range = useMemo(() => getMonthDateRange(month.year, month.month), [month]);
  const isCurrentMonth = today.slice(0, 7) === `${month.year}-${pad(month.month + 1)}`;
  const stepMonth = (delta) => setMonth(({ year, month: m }) => {
    const d = new Date(year, m + delta, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  const [storedOnly, setStoredOnly] = useState(() => readOnlyCurrency(currentId));
  useEffect(() => { setStoredOnly(readOnlyCurrency(currentId)); }, [currentId]);
  const setOnlyCurrency = useCallback((code) => {
    writeOnlyCurrency(currentId, code);
    setStoredOnly(code);
  }, [currentId]);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  // The active currencies, from the workspace's reference data when the
  // app-wide list could not load.
  const [refCurrencies, setRefCurrencies] = useState([]);
  useEffect(() => {
    if (activeCurrencies.length) return undefined;
    let active = true;
    getReferenceData()
      .then((ref) => { if (active) setRefCurrencies((ref.currencies || []).map((c) => c.name)); })
      .catch(() => {});
    return () => { active = false; };
  }, [activeCurrencies.length]);
  const currencies = activeCurrencies.length ? activeCurrencies : refCurrencies;
  // A remembered choice whose currency is no longer active is dropped.
  const onlyCurrency = storedOnly && (!currencies.length || currencies.includes(storedOnly)) ? storedOnly : null;

  // Wait for the display currency's default (it lands in the commit after the
  // active currencies do), so the heavy calls go out once, for the right view.
  const [settled, setSettled] = useState(currencySettled);
  useEffect(() => {
    if (settled) return undefined;
    const done = () => { currencySettled = true; setSettled(true); };
    if (displayCurrency || onlyCurrency) { done(); return undefined; }
    if (!currencies.length) return undefined;
    const id = setTimeout(done, 80);
    return () => clearTimeout(id);
  }, [settled, displayCurrency, onlyCurrency, currencies.length]);

  // "Everything in X" converts into the display currency. With none chosen
  // (no home currency resolved), Home shows one currency's own figures.
  const converted = !onlyCurrency && Boolean(displayCurrency);
  const fallbackCurrency = (homeCurrency && currencies.includes(homeCurrency) ? homeCurrency : null)
    || resolveHomeCurrency(country, currencies)
    || currencies[0];
  const currency = onlyCurrency || displayCurrency || fallbackCurrency || null;
  const convertTarget = displayCurrency || fallbackCurrency;

  // Refetch when the tab comes back and after every save or fix.
  const [focusTick, setFocusTick] = useState(0);
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') setFocusTick((n) => n + 1); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);
  const reloadKey = `${refreshSignal}.${focusTick}`;

  const currencyFilters = useMemo(
    () => (!converted && currency ? [{ entity: 'Movement', column: 'currency', operator: 'in', values: [currency] }] : NO_FILTERS),
    [converted, currency],
  );

  const { data, loading, refetch } = useDashboardData(range, currencyFilters, NO_FILTERS, 'and', { includeReview: false, enabled: settled });

  // Which month and currency the per-currency figures on screen belong to: a
  // step to another month shows skeletons, not the last month's figures,
  // until that month's arrive. A refetch of the same month keeps them.
  const dataKey = `${range.start_date}|${JSON.stringify(currencyFilters)}`;
  const pendingKey = useRef(null);
  const [loadedKey, setLoadedKey] = useState(null);
  useEffect(() => {
    if (loading) pendingKey.current = dataKey;
    else if (pendingKey.current) {
      setLoadedKey(pendingKey.current);
      pendingKey.current = null;
    }
  }, [loading, dataKey]);
  const dataFresh = loadedKey === dataKey;
  // Refetch only when the key moves: a repeated run of the effect (React's
  // development double run) must not fetch twice.
  const lastReload = useRef(reloadKey);
  useEffect(() => {
    if (lastReload.current === reloadKey) return;
    lastReload.current = reloadKey;
    refetch();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey]);

  const categoryMap = useMemo(() => buildCategoryMap(data.categories), [data.categories]);
  const metrics = useMemo(() => transformGroupedDataToMetrics(data, categoryMap), [data, categoryMap]);

  // The converted figures, kept per month so a refetch never blanks them.
  const convertedKey = `${range.start_date}|${displayCurrency}`;
  const [convertedData, setConvertedData] = useState({ key: null, data: null });
  const onConverted = useCallback((d) => setConvertedData({ key: convertedKey, data: d }), [convertedKey]);
  const convertedTotal = convertedData.key === convertedKey ? convertedData.data : null;

  const [trend, setTrend] = useState({ key: null, data: null });
  const trendKey = `${range.start_date}|${converted ? `≈${displayCurrency}` : currency}`;
  useEffect(() => {
    if (!currency || !settled) return undefined;
    let active = true;
    fetchSpendingTrend(range.start_date, range.end_date, converted ? 'all' : currency, [], [], 'and', converted ? displayCurrency : null)
      .then((res) => { if (active) setTrend({ key: trendKey, data: res }); })
      .catch(() => { if (active) setTrend((prev) => (prev.key === trendKey ? prev : { key: trendKey, data: null })); });
    return () => { active = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendKey, reloadKey, settled]);

  const heroLoading = converted ? !convertedTotal : !dataFresh;
  const expenses = converted ? convertedTotal?.expenses : metrics.expenses[currency];
  const income = converted ? convertedTotal?.income : metrics.income[currency];

  const categories = useMemo(() => {
    if (converted) {
      const breakdown = buildConvertedCategoryBreakdown(convertedTotal?.buckets, categoryMap);
      return { rows: breakdown.expenses.slice(0, TOP_CATEGORIES), total: convertedTotal?.expenses || 0 };
    }
    return {
      rows: (metrics.categoryBreakdown.expenses[currency] || []).slice(0, TOP_CATEGORIES),
      total: metrics.expenses[currency] || 0,
    };
  }, [converted, convertedTotal, categoryMap, metrics, currency]);

  // The currencies the converted figures actually came from, for the
  // footnote: another currency with income or expenses, not every one of
  // which was left out as unconvertible.
  const convertedFrom = useMemo(() => {
    if (!converted || !convertedTotal || !dataFresh) return [];
    const counts = new Map();
    (data.summaryByType || []).forEach((row) => {
      if (row.type !== 'Income' && row.type !== 'Expenses') return;
      counts.set(row.currency, (counts.get(row.currency) || 0) + (row.num_movements || 0));
    });
    const left = new Map((convertedTotal.unconvertible || []).map((b) => [b.currency, b.count || 0]));
    return [...counts]
      .filter(([code, count]) => code !== displayCurrency && count > (left.get(code) || 0))
      .map(([code]) => code)
      .sort();
  }, [converted, convertedTotal, dataFresh, data.summaryByType, displayCurrency]);
  const showConvertedNote = converted && convertedTotal?.num_converted > 0 && convertedFrom.length > 0;
  const unconvertibleCount = converted ? convertedTotal?.num_unconvertible || 0 : 0;

  const openCategory = (row) => {
    clearFilters();
    setDateRange(getMonthDateRange(month.year, month.month));
    addFilter({ entity: 'Movement', column: 'category_id', operator: 'in', values: [row.category_id] });
    if (!converted && currency) addFilter({ entity: 'Movement', column: 'currency', operator: 'in', values: [currency] });
    navigate('/movements');
  };

  usePhoneTopBar({
    title: t('navigation:tabs.home'),
    right: currency ? (
      <Box
        as="button"
        type="button"
        onClick={() => setCurrencyOpen(true)}
        aria-haspopup="dialog"
        aria-label={t('phone.currency.pillLabel', { currency })}
        h="44px"
        px="4px"
        display="flex"
        alignItems="center"
      >
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          gap="3px"
          h="32px"
          pl="12px"
          pr="9px"
          borderRadius="full"
          border="1px solid"
          borderColor="redesign.border"
          bg="redesign.bg.card"
          fontSize="14px"
          fontWeight="600"
          color="redesign.heading"
        >
          {currency}
          <Box as={LuChevronDown} boxSize="16px" color="phone.muted" />
        </Box>
      </Box>
    ) : null,
  });

  // Rows in another currency get their converted line, as on every phone list.
  return (
    <ConvertedAmountsProvider>
      <Box pb="8px">
        <SetupChip />
        <PeriodBar month={month} onStep={stepMonth} canNext={!isCurrentMonth} />

        <ConvertedLoader
          key={`${reloadKey}|${range.start_date}`}
          range={range}
          enabled={converted}
          filters={NO_FILTERS}
          onData={onConverted}
        />

        <Hero
          month={month}
          today={today}
          currency={currency}
          expenses={expenses}
          income={income}
          loading={heroLoading}
          trend={trend.key === trendKey ? trend.data : null}
        />

        <ReviewRow onOpen={() => setReviewOpen(true)} />

        <Balances
          range={range}
          onlyCurrency={converted ? null : currency}
          reloadKey={reloadKey}
          onOpen={(card) => openAccount(card.accountId, { currency: card.currency })}
          onSeeAll={() => navigate('/accounts')}
        />

        <Recent
          onlyCurrency={converted ? null : currency}
          homeCurrency={rowHomeCurrency}
          reloadKey={reloadKey}
          ready={settled}
          // Movements as it opens, whatever a category tap here left behind.
          onSeeAll={() => {
            clearFilters();
            setDateRange(getCurrentDateRange(DATE_MODES.MONTH));
            navigate('/movements');
          }}
        />

        <WhereItWent
          rows={categories.rows}
          total={categories.total}
          currency={currency}
          loading={converted ? !convertedTotal : !dataFresh}
          onCategory={openCategory}
          onAll={() => navigate('/categories')}
        />

        <Goals reloadKey={reloadKey} />

        {(showConvertedNote || unconvertibleCount > 0) && (
          <Box px="16px" pt="6px" pb="8px">
            {showConvertedNote && (
              <Text fontSize="13px" lineHeight="18px" color="phone.muted">
                {t('phone.convertedNoteCurrencies', { currencies: convertedFrom.join(', ') })}
              </Text>
            )}
            {unconvertibleCount > 0 && (
              // The info button grows to a thumb-sized target, and the note
              // takes the phone's alert colour, which reads on both grounds.
              <Box
                sx={{
                  '& p': { color: 'phone.alert' },
                  '& button': {
                    minW: '44px', minH: '44px', justifyContent: 'center', ml: '-10px', color: 'phone.alert',
                  },
                }}
              >
                <UnconvertibleNote buckets={convertedTotal.unconvertible} count={unconvertibleCount} fontSize="13px" />
              </Box>
            )}
          </Box>
        )}

        <CurrencySheet
          isOpen={currencyOpen}
          onClose={() => setCurrencyOpen(false)}
          target={convertTarget}
          activeCurrencies={currencies}
          converted={converted}
          onlyCurrency={onlyCurrency || (!converted ? currency : null)}
          onConverted={() => { setOnlyCurrency(null); setDisplayCurrency(convertTarget); }}
          onOnly={(code) => setOnlyCurrency(code)}
        />
        <PhoneReviewSheet isOpen={reviewOpen} onClose={() => setReviewOpen(false)} />
        {accountSheet}
      </Box>
    </ConvertedAmountsProvider>
  );
}
